import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { useStacklineTheme } from '@stackline/ui';
import { useHistory, useLocation } from 'src/utils/Hooks';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { AdjustmentDownloadTable } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/AdjustmentDownloadTable';
import ExportDownloadTable from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/ExportDownloadTable';
import { BEACON_PRO_PAGE_MARGIN_BOTTOM } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import { NotificationBadge } from 'src/components/common/NotificationBadge';

export enum DownloadsTab {
  ExportsDownload = 'exportsDownload',
  AdjustmentsDownload = 'adjustmentsDownload'
}

interface DownloadTabsProps {
  tab: DownloadsTab;
  setTab: (tab: DownloadsTab) => void;
}

const isAdjustmentPage = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('tab') === 'adjustmentsDownload';
};

const DownloadsTabs = ({ tab, setTab }: DownloadTabsProps) => {
  const history = useHistory();
  const urlParams = new URLSearchParams(history.location.search);
  const { notificationBadges } = useNotificationsBadge();

  const handleNavigation = (tabInfo: DownloadsTab.AdjustmentsDownload | DownloadsTab.ExportsDownload) => {
    setTab(tabInfo);
    urlParams.set('tab', tabInfo);
    history.push({ search: urlParams.toString() });
  };

  return (
    <GenericNavigationTabs
      tab={tab}
      tabs={[
        {
          label: (
            <div>
              General
              {notificationBadges.export && (
                <NotificationBadge variant="small" />
              )}
            </div>
          ),
          onClick: () => handleNavigation(DownloadsTab.ExportsDownload),
          value: DownloadsTab.ExportsDownload
        },
        {
          label: (
            <div>
              Adjustments
              {notificationBadges.adjustments && (
                <NotificationBadge variant="small" />
              )}
            </div>
          ),
          onClick: () => handleNavigation(DownloadsTab.AdjustmentsDownload),
          value: DownloadsTab.AdjustmentsDownload
        }
      ]}
    />
  );
};

const DownloadsPageLayout = () => {
  const [tab, setTab] = useState(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const theme = useStacklineTheme();

  useEffect(() => {
    if (isAdjustmentPage()) {
      setTab(DownloadsTab.AdjustmentsDownload);
    } else {
      setTab(DownloadsTab.ExportsDownload);
    }
  }, [searchParams]);

  return (
    <Box width="100%">
      <Text variant="h2">Download List</Text>
      <DownloadsTabs tab={tab} setTab={setTab} />
      <Box marginTop={theme.spacing.lg} marginBottom={BEACON_PRO_PAGE_MARGIN_BOTTOM}>
        {isAdjustmentPage() ? <AdjustmentDownloadTable /> : <ExportDownloadTable />}
      </Box>
    </Box>
  );
};

export default DownloadsPageLayout;
